import type { ReactNode } from 'react';
import { Button, Flex, message } from 'antd';
import { CopyOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { AssetLogListItem } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.response.dto';
import { AssetLogDirectionLabel } from './AssetLogDirectionLabel';
import { AssetLogJsonPayload } from './AssetLogJsonPayload';
import {
  copyAssetLogPayload,
  summarizeAssetLogContent,
} from './assetLogContent.utils';
import styles from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable.module.css';

type Props = {
  log: AssetLogListItem;
  onEdit?: (log: AssetLogListItem) => void;
};

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <div className={styles.detailValue}>{children}</div>
    </div>
  );
}

export function AssetLogExpandedDetails({ log, onEdit }: Props) {
  const { t } = useTranslation('assets');
  const summary = summarizeAssetLogContent(log.rawLog);

  const handleCopy = async () => {
    const ok = await copyAssetLogPayload(summary.payload);
    if (ok) {
      message.success(t('detail.logs.copySuccess', 'Payload copied'));
      return;
    }
    message.error(t('detail.logs.copyError', 'Failed to copy payload'));
  };

  return (
    <div className={styles.expandedPanel}>
      <section className={styles.detailCard}>
        <div className={styles.detailHeader}>
          <Flex align="center" gap={8}>
            <UnorderedListOutlined aria-hidden />
            <h4 className={styles.detailTitle}>
              {t('detail.logs.quickDetails', 'Quick details')}
            </h4>
          </Flex>
          {onEdit ? (
            <Button type="link" size="small" onClick={() => onEdit(log)}>
              {log.isTreated ? t('viewRawLog') : t('editRawLog')}
            </Button>
          ) : null}
        </div>
        <div className={styles.detailBody}>
          <DetailRow label={t('detail.logs.fields.source', 'Source')}>
            {summary.source ?? '—'}
          </DetailRow>
          <DetailRow label={t('detail.logs.fields.action', 'Action')}>
            {summary.action ?? '—'}
          </DetailRow>
          <DetailRow label={t('detail.logs.fields.frameType', 'Frame type')}>
            {summary.frameType ?? '—'}
          </DetailRow>
          <DetailRow label={t('detail.logs.fields.direction', 'Direction')}>
            <AssetLogDirectionLabel direction={summary.direction} />
          </DetailRow>
          <DetailRow label={t('detail.logs.fields.uniqueId', 'UniqueId')}>
            <span className={styles.detailMono}>{summary.uniqueId ?? '—'}</span>
          </DetailRow>
          <DetailRow label={t('detail.logs.fields.connectorId', 'ConnectorId')}>
            {summary.connectorId ?? '—'}
          </DetailRow>
          {summary.meterValueCount !== null ? (
            <DetailRow
              label={t(
                'detail.logs.fields.meterValueCount',
                'MeterValue count',
              )}
            >
              {summary.meterValueCount}
            </DetailRow>
          ) : null}
          <DetailRow label={t('detail.logs.fields.logId', 'Log ID')}>
            <span className={styles.detailMono}>{log.logId}</span>
          </DetailRow>
        </div>
      </section>

      <section className={styles.payloadCard}>
        <div className={styles.payloadHeader}>
          <h4 className={styles.payloadTitle}>
            {t('detail.logs.payloadTitle', 'Payload (JSON)')}
          </h4>
          <Button
            type="default"
            size="small"
            icon={<CopyOutlined />}
            className={styles.payloadCopyButton}
            onClick={() => {
              void handleCopy();
            }}
          >
            {t('detail.logs.copy', 'Copy')}
          </Button>
        </div>
        <div className={styles.payloadBody}>
          <AssetLogJsonPayload value={summary.payload} maxHeight={320} />
        </div>
      </section>
    </div>
  );
}
